/**
 * Détection de passage de niveau et génération du coffre associé.
 *
 * Vivait dans `appContext.updateCurrentChildPoints`, donc ne voyait QUE l'enfant
 * sélectionné. Un parent qui valide depuis la vue « Tous » faisait monter de
 * niveau des enfants que le store ne regardait pas : leur coffre n'était jamais
 * généré, et rien ne rattrapait ensuite (seul un delta observé en direct
 * déclenche la détection, jamais l'état). Mesuré en prod : 85 % des niveaux
 * couverts dans les foyers à 1 enfant, 37 % dans les foyers à 2 enfants et plus.
 *
 * D'où cette fonction libre, appelable pour N'IMPORTE QUEL enfant, par tous les
 * chemins qui font bouger des points.
 */

import { levelFromXp } from '@/composables/useLevels';
import { isScreenshotEmail } from '@/services/screenshot-accounts.service';
import { useAnalytics } from '@/composables/useAnalytics';
import { useAuthStore } from '@/stores/auth';
// Import statique et non dynamique : deux enfants qui montent de niveau dans le
// même tour de boucle déclenchaient deux `import()` concurrents du même module,
// et le second pouvait ne jamais se résoudre. Le service est de toute façon déjà
// dans le graphe de boot via le store `chests`.
import { chestService } from '@/services/chest.service';

// Garde anti-doublon en mémoire, `${childId}-${level}` : plusieurs chemins
// (optimiste, refresh, Realtime) peuvent observer le même passage de niveau.
// La déduplication en base existe aussi côté `generateChest`, celle-ci évite
// juste les requêtes inutiles.
const processedChestGenerations = new Set<string>();

/** Test-only : vide la garde entre deux cas. */
export function __resetLevelChestGuard(): void {
  processedChestGenerations.clear();
}

/**
 * Génère le coffre de niveau si les points franchissent un palier.
 * Non bloquant et silencieux : aucun appelant ne doit échouer à cause d'un coffre.
 *
 * @param childId Enfant concerné, pas forcément celui sélectionné
 * @param oldPoints Points AVANT le changement
 * @param newPoints Points APRÈS le changement
 */
export function checkLevelUpChest(childId: string, oldPoints: number, newPoints: number): void {
  if (!childId) return;

  const oldLevel = levelFromXp(oldPoints);
  const newLevel = levelFromXp(newPoints);
  if (newLevel <= oldLevel) return;

  // Comptes screenshot : les complétions semées peuvent déclencher un niveau,
  // et la modale de coffre passerait par-dessus les captures.
  try {
    if (isScreenshotEmail(useAuthStore().user?.email)) return;
  } catch {
    // Store d'auth injoignable (tests) : on continue, ce garde-fou n'est pas un
    // invariant de correction.
  }

  const chestKey = `${childId}-${newLevel}`;
  if (processedChestGenerations.has(chestKey)) return;
  processedChestGenerations.add(chestKey);

  if (processedChestGenerations.size > 100) {
    const entries = Array.from(processedChestGenerations);
    entries.slice(0, entries.length - 100).forEach(key => processedChestGenerations.delete(key));
  }

  void (async () => {
    try {
      await useAnalytics().logLevelUp(childId, newLevel, oldLevel, newPoints);
    } catch (analyticsError) {
      console.warn('[levelChest] Analytics level_up failed (non-critical):', analyticsError);
    }

    try {
      // La notification `chest_available` est créée par le trigger DB à l'INSERT.
      await chestService.generateChest(childId, 'level', newLevel);
    } catch (chestError) {
      // Le palier n'a pas été honoré : on relâche la garde pour qu'un prochain
      // changement de points puisse retenter.
      processedChestGenerations.delete(chestKey);
      const message = chestError instanceof Error ? chestError.message : '';
      const isDuplicate =
        message.includes('duplicate key') ||
        message.includes('unique constraint') ||
        message.includes('already exists');
      if (!isDuplicate) {
        console.error('[levelChest] Error generating level-up chest:', chestError);
      }
    }
  })();
}
